import React, { useEffect, useState, useContext, useCallback } from 'react';
import * as R from 'ramda';
import styles from './css/PayInvoice.scss';
import Paper from '@mui/material/Paper';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import PaymentForm from './PaymentForm';
import Review from './Review';
import PaymentSuccess from './PaymentSuccess';
import { postJSONPS } from '@cd/utils';
import { either, noop } from '@cd/utils';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { RouteWithProps, useFunctionAsState } from '../utilsReact';
import ZipCodeForm from './ZipCodeForm';
import { CenterProgress } from '../component/CenterProgress';
import { store } from './store';
import { HideWhenRoute } from './HideWhenRoute';
import { useMutationFetch } from './useQueryFetch';
import { Box } from '@mui/material';

const layoutSx = theme => ({
	width: 'auto',
	marginLeft: theme.spacing(2),
	marginRight: theme.spacing(2),
	[theme.breakpoints.up(600 + theme.spacing(2) * 2)]: {
		width: 600,
		marginLeft: 'auto',
		marginRight: 'auto'
	}
});
const paperSx = theme => ({
	marginTop: theme.spacing(3),
	marginBottom: theme.spacing(3),
	padding: theme.spacing(2),
	[theme.breakpoints.up(600 + theme.spacing(3) * 2)]: {
		marginTop: theme.spacing(6),
		marginBottom: theme.spacing(6),
		padding: theme.spacing(3)
	}
});
const stepperSx = theme => ({
	padding: theme.spacing(3, 0, 5)
});
const buttonsSx = {
	display: 'flex',
	justifyContent: 'flex-end'
};
const buttonSx = theme => ({
	marginTop: theme.spacing(3),
	marginLeft: theme.spacing(1)
});

const defs = [
	{ stepRoute: 'payment', stepName: 'Card Details' },
	{ stepRoute: 'review', stepName: 'Review your payment' }
];

const steps = R.pluck('stepName', defs);
const routes = R.pluck('stepRoute', defs);

const makeURL = R.curry((id, routeName) => `/account/pay/${id}/${routeName}`);

const getActiveStepByRoute = routeName =>
	R.findIndex(R.equals(routeName), routes);

const getNextRoute = (id, routeName) =>
	R.compose(
		makeURL(id),
		R.nth(R.__, routes),
		activeStep =>
			activeStep < defs.length - 1 ? activeStep + 1 : activeStep,
		getActiveStepByRoute
	)(routeName);

const getPrevRoute = (id, routeName) =>
	R.compose(
		makeURL(id),
		R.nth(R.__, routes),
		activeStep => (activeStep > 0 ? activeStep - 1 : activeStep),
		getActiveStepByRoute
	)(routeName);

const goToInvoiceList = history => e => history.push('/account/invoices/list');

const FORM_DATA = {
	nameOnCard: '',
	zip: '',
	email: ''
};

function PayInvoice({
	match: {
		params: { id }
	},
	postError = noop
}) {
	const { dispatch, state } = useContext(store);
	const [isCardValid, setIsCardValid] = useState(false);
	const [formData, setFormData] = useState(FORM_DATA);
	const [defaultFormData, setDefaultFormData] = useState(FORM_DATA);
	const [errors, setErrors] = useState({});

	const onValidationError = useCallback(errors => {
		setErrors({ ...errors });
	}, []);

	const onHandleChange = useCallback(values => {
		setFormData(values);
	}, []);

	const [onPayHandler, registerOnPayHandler] = useFunctionAsState(null);
	const [invoices, setInvoices] = useState(null);
	const {
		params: { routeName, extra = '' }
	} = useRouteMatch('/account/pay/:id/:routeName/:extra?');
	const activeStep = getActiveStepByRoute(routeName);
	const history = useHistory();
	const { mutate } = useMutationFetch('/account/get/email', {
		onSuccess: data => {
			if (data) {
				setDefaultFormData(R.mergeRight(formData, data));
				setFormData(R.mergeRight(formData, data));
			}
		}
	});

	const handleNext = e => {
		if (getActiveStepByRoute(routeName) === routes.length - 1) {
			dispatch({ type: 'start processing payment' });
			setTimeout(() => {
				onPayHandler({
					state,
					...formData,
					secret: invoices && invoices[0] && invoices[0].intent
				});
			}, 10);
		} else {
			history.push(getNextRoute(id, routeName));
		}
	};

	useEffect(() => {
		dispatch({ type: 'stop processing payment' });
	}, []);

	const handleBack = () => {
		history.push(getPrevRoute(id, routeName));
	};

	useEffect(() => {
		postJSONPS('GET', `/invoice/payment/get/${id}`).then(
			either(e => {
				dispatch({ type: 'post error', e: e });
				// Stay in the Account tab so the error toast is visible;
				// pushing '/' bounced appboard to the Dashboard and hid it.
				history.push('/account/invoices/list');
			}, setInvoices)
		);
		switch (routeName) {
			case 'payment':
				history.push(makeURL(id, routes[0]));
				break;
			case 'receipt':
				history.push(
					R.compose(url => `${url}/${extra}`, makeURL)(id, 'receipt')
				);
				break;
			default:
				history.push(makeURL(id, routes[0]));
				break;
		}
		return () => {};
	}, []);

	const onValid = valid => {
		setIsCardValid(valid);
	};

	const disableNext =
		((!R.isEmpty(errors) ||
			formData.zip.length < 5 ||
			formData.nameOnCard.length < 3 ||
			formData.email.length < 3) &&
			(routeName === 'payment' || routeName === 'review')) ||
		state.payment.processing ||
		!isCardValid;

	console.log('disableNext', disableNext);

	return invoices === null ? (
		<CenterProgress />
	) : (
		<React.Fragment>
			<Box component="main" sx={layoutSx}>
				<Paper sx={paperSx} elevation={2}>
					<div className={styles.transitionReview}>
						<Typography component="h1" variant="h4" align="center">
							{activeStep >= 0 ? 'Payment Form' : 'Confirmation'}
						</Typography>

						{activeStep >= 0 && (
							<Stepper
								activeStep={activeStep}
								sx={stepperSx}>
								{steps.map(label => (
									<Step key={label}>
										<StepLabel>{label}</StepLabel>
									</Step>
								))}
							</Stepper>
						)}
						<HideWhenRoute
							currentRoute={routeName}
							routes={['review', 'receipt']}>
							<>
								<RouteWithProps
									path={[
										'/account/pay/:id/payment',
										'/account/pay/:id/review'
									]}
									render={props => (
										<>
											<PaymentForm {...props} />
											<Box sx={{ p: 1, mb: -2 }}>
												<Button
													sx={{ mb: 1 }}
													onClick={e => mutate()}
													variant="outlined"
													size="small">
													FILL CONTACT INFO
												</Button>
											</Box>
											<ZipCodeForm {...props} />
										</>
									)}
									componentProps={{
										setFormData,
										defaultFormData,
										onValid,
										registerOnPayHandler,
										onHandleChange,
										onValidationError
									}}
								/>
							</>
						</HideWhenRoute>
						<RouteWithProps
							path="/account/pay/:id/review"
							component={Review}
							componentProps={{ invoices }}
						/>

						<RouteWithProps
							path="/account/pay/success/receipt/:transactionID"
							component={PaymentSuccess}
						/>

						<Box component="div" sx={buttonsSx}>
							{activeStep > 0 && activeStep < 3 && (
								<Button
									onClick={handleBack}
									sx={buttonSx}>
									Back
								</Button>
							)}
							<Button
								loading={state.payment.processing}
								disabled={disableNext}
								variant="contained"
								color="primary"
								onClick={
									activeStep >= 0
										? handleNext
										: goToInvoiceList(history)
								}
								sx={buttonSx}>
								{routeName === 'review'
									? 'PAY'
									: routeName === 'receipt'
									? 'DONE'
									: 'Next'}
							</Button>
						</Box>
					</div>
				</Paper>
			</Box>
		</React.Fragment>
	);
}

export default PayInvoice;
